/**
 * RisicoBundleCard — alle openstaande risico-meldingen op één Werk_Order
 * in de office-inbox. Eén AI-draft + één Verstuur-knop voor de hele bundle.
 *
 * Bij mount fetchen we de AI-draft (Claude, multi-kleed prompt). Office
 * reviewt + edit subject/body, klikt Verstuur → POST /api/risico/send →
 * Gmail-send + Apex markBundleVerstuurd → alle Gemeld-WI's op die WO worden
 * Wacht_klant in één transactie.
 */
import { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription, Button, Badge } from '../ui';
import {
  generateRisicoDraft,
  sendRisicoMail,
  polishToelichting,
  saveManualToelichting,
  type RisicoBundle,
  type RisicoBundleItem,
  type RisicoDraft,
} from '../../lib/risico';
import { useUserRole } from '../../hooks/useUserRole';
import { SpecialistAkkoordExpander } from './SpecialistAkkoordExpander';

interface Props {
  bundle: RisicoBundle;
  onSent: () => void;
}

function formatGemeldOp(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  const hours = Math.floor(ms / (1000 * 60 * 60));
  if (hours < 1) return 'net binnen';
  if (hours < 24) return `${hours}u geleden`;
  const days = Math.floor(hours / 24);
  return `${days}d geleden`;
}

export function RisicoBundleCard({ bundle, onSent }: Props) {
  const { role } = useUserRole();
  const canOverride = role === 'admin' || role === 'medewerker';

  const [localItems, setLocalItems] = useState<RisicoBundleItem[]>(bundle.items);
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);

  const [draft, setDraft] = useState<RisicoDraft | null>(null);
  const [draftLoading, setDraftLoading] = useState(true);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDraftLoading(true);
    setDraftError(null);
    generateRisicoDraft(bundle.workOrderId)
      .then((d) => {
        if (cancelled) return;
        setDraft(d);
        setSubject(d.subject);
        setBody(d.body);
      })
      .catch((err) => {
        if (!cancelled) setDraftError(err instanceof Error ? err.message : 'AI-draft mislukt');
      })
      .finally(() => {
        if (!cancelled) setDraftLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bundle.workOrderId]);

  const canSend = !!subject.trim() && body.trim().length >= 20 && !sending;
  const itemCount = localItems.length;

  const handleSpecialistSuccess = (wasserijItemId: string) => {
    setExpandedItemId(null);
    setLocalItems((prev) => {
      const next = prev.filter((it) => it.wasserijItemId !== wasserijItemId);
      if (next.length === 0) {
        // Laatste item afgehandeld → bundle weg uit inbox
        onSent();
      }
      return next;
    });
  };

  const handleSend = async () => {
    setSending(true);
    setSendError(null);
    try {
      await sendRisicoMail({
        workOrderId: bundle.workOrderId,
        subject: subject.trim(),
        body: body.trim(),
        aiDraft: draft?.body,
      });
      onSent();
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Verzenden mislukt');
    } finally {
      setSending(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle>
              {bundle.klantNaam || 'Onbekende klant'}
              {bundle.workOrderNummer ? ` · ${bundle.workOrderNummer}` : ''}
            </CardTitle>
            <CardDescription>
              {itemCount} {itemCount === 1 ? 'kleed' : 'kleden'} met risico
              {bundle.oudsteGemeldOp ? ` · oudste ${formatGemeldOp(bundle.oudsteGemeldOp)}` : ''}
            </CardDescription>
          </div>
          <Badge variant="default">{itemCount}×</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Per-kleed lijst */}
        <div className="space-y-2">
          {localItems.map((item) => (
            <div
              key={item.wasserijItemId}
              className="rounded-md border border-yellow-200 bg-yellow-50 p-3 space-y-2"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="text-sm font-medium text-gray-900">
                  {item.kleedNaam}{' '}
                  <span className="font-normal text-gray-500">({item.wiNummer})</span>
                </div>
                {item.fotosUrl && (
                  <a
                    href={item.fotosUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="shrink-0 text-xs text-blue-600 hover:underline"
                  >
                    📷 Foto's
                  </a>
                )}
              </div>
              {item.risicoTypes.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {item.risicoTypes.map((t) => (
                    <Badge key={t} variant="default">
                      {t}
                    </Badge>
                  ))}
                </div>
              )}
              <PolishBlock item={item} />
              {canOverride && expandedItemId !== item.wasserijItemId && (
                <div className="flex">
                  <button
                    type="button"
                    onClick={() => setExpandedItemId(item.wasserijItemId)}
                    className="text-xs text-emerald-700 hover:underline"
                  >
                    Specialist akkoord (geen klant-mail)
                  </button>
                </div>
              )}
              {canOverride && expandedItemId === item.wasserijItemId && (
                <SpecialistAkkoordExpander
                  wasserijItemId={item.wasserijItemId}
                  kleedNaam={item.kleedNaam}
                  onSuccess={handleSpecialistSuccess}
                  onCancel={() => setExpandedItemId(null)}
                />
              )}
            </div>
          ))}
        </div>

        {/* Klant-email */}
        {bundle.klantEmail ? (
          <p className="text-xs text-gray-500">→ {bundle.klantEmail}</p>
        ) : (
          <p className="text-xs text-red-600">⚠️ Geen klant-email gevonden — check Account.PersonEmail</p>
        )}

        {/* AI draft */}
        {draftLoading ? (
          <div className="rounded-md border border-gray-200 bg-gray-50 p-4 text-center text-sm text-gray-600">
            AI-draft wordt gegenereerd…
          </div>
        ) : draftError ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {draftError}
          </div>
        ) : (
          <>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Onderwerp</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                disabled={sending}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Bericht</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={8}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:border-blue-500 focus:outline-none resize-y"
                disabled={sending}
              />
              <p className="mt-1 text-xs text-gray-500">
                Per-kleed sectie + magic-link knop "Bekijk je keuzes" worden automatisch toegevoegd.
                {draft?.model ? ` · ${draft.model}` : ''}
                {draft?.confidence ? ` · confidence ${(draft.confidence * 100).toFixed(0)}%` : ''}
              </p>
            </div>
          </>
        )}

        {sendError && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {sendError}
          </div>
        )}
      </CardContent>
      <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-3">
        <Button
          onClick={handleSend}
          disabled={!canSend || draftLoading || !!draftError || itemCount === 0}
        >
          {sending
            ? 'Versturen…'
            : `Verstuur naar klant${itemCount > 1 ? ` (${itemCount} kleden)` : ''}`}
        </Button>
      </div>
    </Card>
  );
}

/**
 * Toont de klantvriendelijke polish + origineel + refresh/edit-knop.
 * Optimistic update bij refresh — geen volledige bundle-reload nodig.
 */
function PolishBlock({ item }: { item: RisicoBundleItem }) {
  const [polish, setPolish] = useState<string | null>(item.toelichtingKlantvriendelijk);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(item.toelichtingKlantvriendelijk ?? '');
  const [showOriginal, setShowOriginal] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  const hasOriginal = !!item.toelichting && item.toelichting.trim().length > 0;
  const display = polish ?? item.toelichting;

  const handleRefresh = async () => {
    setRefreshing(true);
    setRefreshError(null);
    try {
      const result = await polishToelichting(item.wasserijItemId, true);
      if (result.success && result.polished) {
        setPolish(result.polished);
        setEditText(result.polished);
      } else {
        setRefreshError(result.error || 'Polish faalde');
      }
    } catch (err) {
      setRefreshError(err instanceof Error ? err.message : 'Polish faalde');
    } finally {
      setRefreshing(false);
    }
  };

  const handleSaveEdit = async () => {
    const trimmed = editText.trim();
    if (trimmed.length < 10) {
      setRefreshError('Tekst moet minstens 10 tekens zijn');
      return;
    }
    setRefreshing(true);
    setRefreshError(null);
    try {
      const result = await saveManualToelichting(item.wasserijItemId, trimmed);
      if (result.success) {
        setPolish(trimmed);
        setEditing(false);
      } else {
        setRefreshError(result.error || 'Save mislukt');
      }
    } catch (err) {
      setRefreshError(err instanceof Error ? err.message : 'Save mislukt');
    } finally {
      setRefreshing(false);
    }
  };

  if (!hasOriginal) return null;

  return (
    <div className="space-y-1">
      {editing ? (
        <div className="space-y-2">
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
          />
          <div className="flex gap-2 text-xs">
            <button
              type="button"
              onClick={handleSaveEdit}
              disabled={refreshing}
              className="rounded bg-blue-600 px-2 py-1 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {refreshing ? 'Opslaan…' : 'Opslaan'}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setEditText(polish ?? '');
                setRefreshError(null);
              }}
              className="rounded border border-gray-300 px-2 py-1 text-gray-700 hover:bg-gray-50"
            >
              Annuleren
            </button>
          </div>
        </div>
      ) : (
        display && (
          <p className="text-sm text-gray-800 italic whitespace-pre-wrap">"{display}"</p>
        )
      )}

      {!editing && (
        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
          {polish ? (
            <span className="text-emerald-700">✓ Klantvriendelijk gepolijst</span>
          ) : (
            <span className="text-amber-700">⚠ Nog niet gepolijst (toont origineel)</span>
          )}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            className="text-blue-600 hover:underline disabled:opacity-50"
          >
            {refreshing ? 'Bezig…' : polish ? 'Opnieuw polishen' : 'Polish nu'}
          </button>
          <button
            type="button"
            onClick={() => {
              setEditText(polish ?? item.toelichting ?? '');
              setEditing(true);
            }}
            className="text-blue-600 hover:underline"
          >
            Bewerk
          </button>
          <button
            type="button"
            onClick={() => setShowOriginal((v) => !v)}
            className="text-gray-500 hover:underline"
          >
            {showOriginal ? 'Verberg origineel' : 'Toon origineel'}
          </button>
        </div>
      )}

      {showOriginal && item.toelichting && (
        <p className="rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-600">
          <span className="font-medium text-gray-500">Origineel: </span>
          {item.toelichting}
        </p>
      )}

      {refreshError && (
        <p className="text-xs text-red-600">{refreshError}</p>
      )}
    </div>
  );
}
